import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  OnDestroy
} from "@angular/core";
import {
  NG_VALUE_ACCESSOR,
  ControlValueAccessor,
  Validator,
  AbstractControl,
  ValidationErrors,
  NG_VALIDATORS
} from "@angular/forms";
import { BarcodeScannerLivestreamComponent } from "ngx-barcode-scanner";

const noop = () => {};

@Component({
  selector: "form-input",
  templateUrl: "./input.component.html",
  styleUrls: ["./input.component.scss"],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: InputComponent
    }
  ]
})
export class InputComponent
  implements OnInit, OnDestroy, ControlValueAccessor, Validator
{
  public isFocused = false;

  @ViewChild("input") inputElement: ElementRef;
  @ViewChild("inputTextarea") textareaElement: ElementRef;
  @Input() public type = "text";
  @Input() public disabled = "false";
  @Input() public autofocus = "false";
  @Input() public green = false;
  @Input() public error = false;
  @Input() public placeholder = "";
  @Input() public pattern = null;
  @Input() public required = "false";
  @Input() public date = false;
  @Input() public inputmode = "";

  @Input() public scannerSuccessVisible: boolean = false;
  @Input() public scannerErrorVisible: boolean = false;

  @Input() public step = "";
  _min = "";
  @Input() public set min(val: any) {
    this._min = val;
    this.onChange();
  }
  _max = "";
  @Input() public set max(val: any) {
    this._max = val;
    this.onChange();
  }

  @Input() public plus = null;
  @Input() public plusOnMobile = null;
  @Input() public search = null;
  @Input() public searchOnMobile = null;

  @Input() public actionLabel = null;

  @Input() public autocomplete: any[] | null = null;
  @Input() public autocompleteProperty: string = "label";

  @Input() public icon: string = null;
  @Input() public iconHref: string = null;

  @Input() public textarea: boolean = false;

  @Input() public maxLength: number | null = null;
  public lengthLimitExceeded: boolean = false;

  private static ID: number = 0;
  public id: number;

  @Input() public value!: any;
  @Output() public valueChange = new EventEmitter<any>();
  @Output() public invalid = new EventEmitter<any>();
  @Output() public valid = new EventEmitter<any>();
  @Output() public onBlur = new EventEmitter<any>();
  @Output() public onFocus = new EventEmitter<any>();
  @Output() public enter = new EventEmitter<any>();
  @Output() public action = new EventEmitter<any>();
  @Output() public onClick = new EventEmitter<any>();
  @Output() public afterContentChange = new EventEmitter<any>();

  constructor() {
    this.id = InputComponent.ID++;
  }

  get isValid() {
    return !this.lastInvalid;
  }

  lastInvalid = false;
  emitInvalid() {
    if (!this.lastInvalid) {
      this.lastInvalid = true;
      this.invalid.emit();
    }
  }
  emitValid() {
    if (this.lastInvalid) {
      this.lastInvalid = false;
      this.valid.emit();
    }
  }

  validate(control: AbstractControl): ValidationErrors {
    if (
      (this.required as any) === true ||
      this.required === "true" ||
      this.required === ""
    ) {
      if (
        this.value == null ||
        this.value == undefined ||
        this.value === "" ||
        (this.value && this.value.trim && this.value.trim().length == 0)
      ) {
        this.emitInvalid();
        return { required: this.value };
      }
      if (this.type == "number") {
        if (this._min && this._min != "") {
          if (+this.value < +this._min) {
            this.emitInvalid();
            return { number: this.value };
          }
        }
        if (this._max && this._max != "") {
          if (+this.value > +this._max) {
            this.emitInvalid();
            return { number: this.value };
          }
        }
      }
    }
    if (this.step && +this.step && this.value) {
      if (!isStepMatch(+this.value, +this.step)) {
        this.emitInvalid();
        return { number: this.value };
      }
    }

    if (this.pattern) {
      if (!new RegExp(this.pattern, "gi").test(this.value)) {
        this.emitInvalid();
        return { pattern: this.value };
      }
    }
    if (this.dateOutOfRange) {
      this.emitInvalid();
      return { pattern: this.value };
    }
    if (this.maxLength && this.value) {
      if (this.value.toString().length > this.maxLength) {
        this.emitInvalid();
        this.lengthLimitExceeded = true;
        return { lengthLimitExceeded: true };
      }
    }
    this.lengthLimitExceeded = false;
    this.emitValid();
    return;
  }
  validatorChange: () => void = noop;
  registerOnValidatorChange?(fn: () => void): void {
    this.validatorChange = fn;
  }

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  ngOnInit(): void {}
  ngOnDestroy(): void {
    this.closeCamera2dScanner();
  }

  public focus() {
    if (this.inputElement) this.inputElement.nativeElement.focus();
    if (this.textareaElement) this.textareaElement.nativeElement.focus();
  }
  public blur() {
    if (this.inputElement) this.inputElement.nativeElement.blur();
    if (this.textareaElement) this.textareaElement.nativeElement.blur();
  }

  onNativeInput(event: InputEvent) {
    if (event.inputType === "insertLineBreak") {
      this.emitEnter(this.Autocomplete);
    }
  }

  private valueChangeTimeout = null;
  onChange(event?, stopPropagation = false) {
    if (this.valueChangeTimeout) {
      clearTimeout(this.valueChangeTimeout);
    }

    this.onChangeCallback(this.value);
    this.validatorChange();
    this.validate(null as any);
    this.valueChangeTimeout = setTimeout(
      () => this.afterContentChange.emit(),
      1200
    );

    if (event) {
      // keep this for desktop browsers (real keyCodes are reliable there)
      if (event.key === "Enter" || event.keyCode === 13) {
        this.emitEnter(this.Autocomplete);
      }
      if (stopPropagation) {
        event.stopPropagation();
      }
    }
  }
  emitBlur() {
    this.onTouchedCallback();
    this.onBlur.emit();
  }
  emitFocus() {
    this.onFocus.emit();
    this.isFocused = true;
  }

  writeValue(value: any) {
    if (value !== this.value) {
      this.value = value;
    }
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }
  emitEnter(autocompleteData = null) {
    var returnValue = undefined;
    if (this.valueChangeTimeout) {
      clearTimeout(this.valueChangeTimeout);
      this.valueChangeTimeout = null;
    }
    if (autocompleteData && autocompleteData.length > 0 && this.isFocused) {
      this.runAutocomplete(this.Autocomplete[0]);
      returnValue = false;
    }
    this.enter.emit();
    this.afterContentChange.emit();
    return returnValue;
  }
  removeFocus() {
    if (this.autocomplete)
      setTimeout(() => {
        this.isFocused =
          document.activeElement === this.inputElement?.nativeElement ||
          document.activeElement === this.textareaElement?.nativeElement;
      }, 200);
    else this.isFocused = false;
  }

  get Autocomplete() {
    if (
      this.autocomplete &&
      this.autocompleteProperty &&
      this.value &&
      this.value.length > 0
    ) {
      return [
        ...new Set(
          this.autocomplete
            .map((a) => a[this.autocompleteProperty]?.trim())
            .filter(
              (a) =>
                a &&
                a.toLowerCase().includes(this.value.toLowerCase()) &&
                a.toLowerCase() != this.value.toLowerCase()
            )
        )
      ];
    }
  }
  runAutocomplete(str: string) {
    console.log("Autocomplete: " + str);
    this.value = str;
    this.onChange();
  }

  emitAction(value: any = null) {
    this.action.emit(value);
  }

  get dateOutOfRange() {
    if (this.type == "date") {
      if (this._min) {
        if (new Date(this._min) > new Date(this.value)) {
          return true;
        }
      }
      if (this._max) {
        if (new Date(this._max) < new Date(this.value)) {
          return false;
        }
      }
    }
    return false;
  }

  @ViewChild("monthInput") monthInput: ElementRef;
  @ViewChild("yearInput") yearInput: ElementRef;

  private dateValue = { day: 1, month: 1, year: 1 };
  dateDayChanged(event) {
    this.dateValue.day = +event.target.value;
    if (this.dateValue.day > 31) {
      this.dateValue.day = Math.floor(this.dateValue.day % 10);
      event.target.value = this.dateValue.day;
    }

    this.dateValueChanged();

    if (
      ((this.dateValue.day > 3 || event.target.value.length > 1) &&
        this.isNumberInput(event)) ||
      event.keyCode == 13
    ) {
      setTimeout(() => {
        this.monthInput.nativeElement.focus();
      }, 30);
    }
  }
  dateMonthChanged(event) {
    this.dateValue.month = +event.target.value;
    if (this.dateValue.month > 12) {
      this.dateValue.month = Math.floor(this.dateValue.month % 10);
      event.target.value = this.dateValue.month;
    }

    this.dateValueChanged();

    if (
      ((this.dateValue.month > 1 || event.target.value.length > 1) &&
        this.isNumberInput(event)) ||
      event.keyCode == 13
    ) {
      setTimeout(() => {
        this.yearInput.nativeElement.focus();
      }, 30);
    }
  }
  dateYearChanged(event) {
    this.dateValue.year = +event.target.value;
    this.dateValueChanged();

    if (
      (this.dateValue.year > 1000 && this.isNumberInput(event)) ||
      event.keyCode == 13
    ) {
      this.emitEnter();
    }
  }
  dateValueChanged() {
    if (this.dateValue.year && this.dateValue.month && this.dateValue.day) {
      const val = new Date(
        this.pad(this.dateValue.year.toString(), 4) +
          "-" +
          this.pad(this.dateValue.month.toString(), 2) +
          "-" +
          this.pad(this.dateValue.day.toString(), 2)
      );
      val.setHours(12);

      this.writeValue(val);
      this.valueChange.emit(val);
      this.onChange();
    }
  }

  private pad(num: string, size: number) {
    while (num.length < size) num = "0" + num;
    return num;
  }

  private isNumberInput(e: KeyboardEvent) {
    var keyCode = e.keyCode || e.which;
    if (keyCode >= 96 && keyCode <= 105) {
      // Numpad keys
      keyCode -= 48;
    }

    return keyCode >= 48 && keyCode <= 57;
  }

  @ViewChild(BarcodeScannerLivestreamComponent)
  barcodeScanner: BarcodeScannerLivestreamComponent;

  @Input() public scanner1D: boolean = false;

  camera2dScannerVisible: boolean = false;
  public enableCamera2dScanner() {
    this.camera2dScannerVisible = true;
    setTimeout(() => {
      this.barcodeScanner.start();
    }, 50);
    document.body.classList.add("hide-nav-mobile");
  }
  public closeCamera2dScanner() {
    this.camera2dScannerVisible = false;
    this.barcodeScanner?.stop();
    document.body.classList.remove("hide-nav-mobile");
  }
  private lastCodeValue: any = null;
  private lastCodeTimestamp: number = 0;
  camera2dScannerValueChanged(value: any) {
    if (
      this.lastCodeValue != value.codeResult.code ||
      Date.now() - this.lastCodeTimestamp > 10000
    ) {
      this.lastCodeValue = value.codeResult.code;
      this.lastCodeTimestamp = Date.now();
      this.writeValue(this.lastCodeValue);
      this.valueChange.emit(value.codeResult.code);
      this.onChange();
      this.emitEnter();
    }
  }
  toggleCamera2dScannerInput() {}
}

function countDecimals(num: number) {
  const s = num.toString();
  if (s.includes("e")) {
    const [base, exp] = s.split("e");
    const dec = (base.split(".")[1] || "").length;
    return Math.max(0, dec - parseInt(exp, 10));
  }
  return (s.split(".")[1] || "").length;
}

function isStepMatch(value: number, step: number) {
  const valueDecimals = countDecimals(value);
  const stepDecimals = countDecimals(step);
  const scale = 10 ** Math.max(valueDecimals, stepDecimals);

  const scaledValue = Math.round(value * scale);
  const scaledStep = Math.round(step * scale);

  return scaledValue % scaledStep === 0;
}
